import React from 'react';
import SoloGames from './SoloGames';
import MultiplayerGames from './MultiplayerGames';
import HtmlGames from './HtmlGames';
import './GameCategories.css';

// Multi-Elemental Arena Configuration
const ARENA_CONFIG = {
  solo: {
    title: 'INFERNO WARRIOR ARENA',
    description: 'Face the molten battlefield alone - where legends are forged in fire and ash',
    icon: '🌋',
    supportingIcons: ['🔥', '⚔️', '💀'],
    className: 'volcano-warrior',
    features: [
      'Earn multiple victory streaks',
      'Unlock legendary fire weapons',
      'Climb the molten leaderboard',
      'Master solo combat tactics',
      'Collect rare lava crystals'
    ]
  },
  multiplayer: {
    title: 'FROZEN BATTLEGROUND',
    description: 'Unite with allies in the eternal winter - teamwork forged in ice and snow',
    icon: '❄️',
    supportingIcons: ['🧊', '⚡', '🛡️'],
    className: 'ice-multiplayer',
    features: [
      'Form unstoppable ice squads',
      'Coordinate frozen assaults',
      'Earn team victory bonuses',
      'Master arctic warfare',
      'Unlock crystal armor sets'
    ]
  },
  arcade: {
    title: 'VERDANT TRAINING GROUND',
    description: 'Sharpen your skills in nature\'s sanctuary - where growth meets power',
    icon: '🌲',
    supportingIcons: ['🍃', '🏹', '🦅'],
    className: 'forest-academy',
    features: [
      'Master nature-based abilities',
      'Unlock forest guardian modes',
      'Earn wilderness survival XP',
      'Complete ranger challenges',
      'Discover hidden tree treasures'
    ],
    isExternal: true,
    externalUrl: 'https://play.uvw.asia'
  }
};

const GameCategories = ({ category, walletData, onNavigate, onBack }) => {
  // Handle external navigation
  const handleNavigation = (categoryKey) => {
    const categoryData = ARENA_CONFIG[categoryKey];
    
    if (categoryData.isExternal && categoryData.externalUrl) {
      // Open external website in a new tab
      window.open(categoryData.externalUrl, '_blank');
    } else {
      // Use internal navigation
      onNavigate(categoryKey);
    }
  };

  // Render specific category games
  if (category !== 'all') {
    const categoryData = ARENA_CONFIG[category];
    
    // If it's an external category, redirect immediately
    if (categoryData.isExternal && categoryData.externalUrl) {
      window.open(categoryData.externalUrl, '_blank');
      // Go back to main menu
      onBack();
      return null;
    }
    
    return (
      <div className="games-deployment">
        <div className="deployment-header">
          <button className="return-button" onClick={onBack}>
            ← RETURN TO BASE
          </button>
          <div className="deployment-info">
            <h2 className="deployment-title">{categoryData.title}</h2>
            <p className="deployment-description">{categoryData.description}</p>
          </div>
        </div>

        <div className="games-container">
          {category === 'solo' && <SoloGames onNavigate={onNavigate} />}
          {category === 'multiplayer' && <MultiplayerGames onNavigate={onNavigate} />}
          {category === 'arcade' && <HtmlGames onNavigate={onNavigate} />}
        </div>
      </div>
    );
  }

  // Main arena selection interface
  return (
    <div className="command-center">
      <div className="command-header">
        <h1 className="command-title">ELEMENTAL GAMING NEXUS</h1>
        <p className="command-subtitle">CHOOSE YOUR ELEMENTAL PATH</p>
      </div>

      <div className="arena-grid">
        {Object.entries(ARENA_CONFIG).map(([key, arena]) => (
          <div 
            key={key}
            className={`arena-card ${arena.className} ${arena.isExternal ? 'external-link' : ''}`}
            onClick={() => handleNavigation(key)}
          >
            <div className="arena-header">
              <div className="arena-icon-cluster">
                <div className="main-icon">{arena.icon}</div>
                <div className="supporting-icons">
                  {arena.supportingIcons.map((icon, index) => (
                    <span key={index}>{icon}</span>
                  ))}
                </div>
              </div>
              <h3 className="arena-title">
                {arena.title}
                {arena.isExternal && <span className="external-indicator"> ↗</span>}
              </h3>
              <p className="arena-description">{arena.description}</p>
            </div>

            <div className="gaming-features">
              <div className="feature-title">GAMING REWARDS & FEATURES</div>
              <ul className="feature-list">
                {arena.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            <div className="arena-footer">
              <button className="enter-arena-btn">
                {arena.isExternal ? 'LAUNCH EXTERNAL ARENA' : 'ENTER ARENA'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="command-briefing">
        <div className="briefing-grid">
          <div className="briefing-card">
            <div className="briefing-icon">🏆</div>
            <h4>ELEMENTAL MASTERY</h4>
            <p>Master fire, ice, and nature elements to unlock exclusive rewards and legendary status across all gaming realms</p>
          </div>
          <div className="briefing-card">
            <div className="briefing-icon">⚡</div>
            <h4>POWER PROGRESSION</h4>
            <p>Level up through elemental tiers, earning unique abilities and accessing higher-stakes gaming tournaments</p>
          </div>
          <div className="briefing-card">
            <div className="briefing-icon">💎</div>
            <h4>LEGENDARY LOOT</h4>
            <p>Collect rare elemental crystals, unlock mythical weapons, and earn blockchain-verified gaming achievements</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameCategories;